import {
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  invalidMovingDateError,
  invalidMovingDateRangeError,
  invalidPostcodeError,
  isMovingDateInAllowedRange,
  isValidMovingDate,
  isValidPostcode,
  requiredFieldError,
} from '../../utils/validationUtils';
import {
  GbgAddressErrors,
  GbgAddressValues,
  ManualAddressErrors,
  ManualAddressValues,
  MovingDateErrors,
  MovingDateValues,
} from '@/src/types/MoveOut';

export type FormSubmissionState =
  | {
      submitted: false;
    }
  | {
      submitted: true;
      isValid: boolean;
      source: FormSubmissionSource;
    };

export type FormValues = {
  movingDate: MovingDateValues;
  gbgAddress: GbgAddressValues;
  manualAddress: ManualAddressValues;
};

type FormErrors = {
  movingDate: MovingDateErrors;
  gbgAddress: GbgAddressErrors;
  manualAddress: ManualAddressErrors;
};

export enum FormSubmissionSource {
  MANUAL_ADDRESS_NEXT = 'MANUAL_ADDRESS_NEXT',
  NO_UK_ADDRESS_NEXT = 'GBG_ADDRESS_NEXT',
  GBG_ADDRESS_GET_QUOTE = 'GBG_ADDRESS_GET_QUOTE',
  GBG_ADDRESS_END_CONTRACT = 'GBG_ADDRESS_END_CONTRACT',
}

type AddressProvider = 'gbg' | 'manual';

export type MovingHomeFormState = {
  formRef: RefObject<HTMLFormElement>;
  formValues: FormValues;
  formErrors: FormErrors;
  addressProvider: AddressProvider;
  formSubmissionState: FormSubmissionState;
  setFormSubmissionState: Dispatch<SetStateAction<FormSubmissionState>>;
  setAddressProvider: Dispatch<SetStateAction<AddressProvider>>;
  getGbgAddressErrors: (values: GbgAddressValues) => GbgAddressErrors;
  getManualAddressErrors: (values: ManualAddressValues) => ManualAddressErrors;
  getMovingDateErrors: (values: MovingDateValues) => MovingDateErrors;
  validateForm: (values: FormValues) => boolean;
  valuesChangeHandler: (
    section: keyof FormValues,
    shouldValidate?: boolean,
  ) => (
    values: MovingDateValues | GbgAddressValues | ManualAddressValues,
  ) => void;
  errorsChangeHandler: (
    section: keyof FormErrors,
  ) => (
    errors: MovingDateErrors | GbgAddressErrors | ManualAddressErrors,
  ) => void;
};

const initialFormValues: FormValues = {
  movingDate: { date: { day: '', month: '', year: '' } },
  gbgAddress: { address: undefined, addressOutsideUk: false, postCode: '' },
  manualAddress: {
    addressLine1: '',
    addressLine2: '',
    town: '',
    houseNumberOrName: '',
    postcode: '',
  },
};

const initialFormErrors: FormErrors = {
  movingDate: {},
  gbgAddress: {},
  manualAddress: {},
};

export function useMovingHomeFormState() {
  const formRef = useRef<HTMLFormElement>(null);

  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
  const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
  const [addressProvider, setAddressProvider] =
    useState<AddressProvider>('gbg');
  const [formSubmissionState, setFormSubmissionState] =
    useState<FormSubmissionState>({ submitted: false });

  const getMovingDateErrors = useCallback((values: MovingDateValues) => {
    const errors: MovingDateErrors = {};
    const { day, month, year } = values.date;

    if (!day || !month || !year) {
      errors.date = requiredFieldError;
    } else if (!isValidMovingDate(day, month, year)) {
      errors.date = invalidMovingDateError;
    } else if (!isMovingDateInAllowedRange(day, month, year)) {
      errors.date = invalidMovingDateRangeError;
    }

    return errors;
  }, []);

  const getGbgAddressErrors = useCallback((values: GbgAddressValues) => {
    const errors: GbgAddressErrors = {};
    const { address, addressOutsideUk, postCode } = values;

    if (!addressOutsideUk) {
      if (!address) {
        errors.address = requiredFieldError;
      }
      if (!postCode) {
        errors.postCode = requiredFieldError;
      } else if (!isValidPostcode(postCode)) {
        errors.postCode = invalidPostcodeError;
      }
    }

    return errors;
  }, []);

  const getManualAddressErrors = useCallback((values: ManualAddressValues) => {
    const errors: ManualAddressErrors = {};
    const { addressLine1, addressLine2, town, houseNumberOrName, postcode } =
      values;

    if (!addressLine1) {
      errors.addressLine1 = requiredFieldError;
    } else if (addressLine1.length > 30) {
      errors.addressLine1 = 'Max 30 characters';
    }

    if (addressLine2 && addressLine2.length > 30) {
      errors.addressLine2 = 'Max 30 characters';
    }

    if (!town) {
      errors.town = requiredFieldError;
    } else if (town.length > 30) {
      errors.town = 'Max 30 characters';
    }

    if (!houseNumberOrName) {
      errors.houseNumberOrName = requiredFieldError;
    } else if (houseNumberOrName.length > 30) {
      errors.houseNumberOrName = 'Max 30 characters';
    }

    if (!postcode) {
      errors.postcode = requiredFieldError;
    } else if (postcode.length > 30) {
      errors.postcode = 'Max 30 characters';
    } else if (!isValidPostcode(postcode)) {
      errors.postcode = invalidPostcodeError;
    }

    return errors;
  }, []);

  const validateForm = useCallback(
    (values: FormValues) => {
      const { movingDate, gbgAddress, manualAddress } = values;

      const errors: FormErrors = {
        movingDate: getMovingDateErrors(movingDate),
        gbgAddress:
          addressProvider === 'gbg' ? getGbgAddressErrors(gbgAddress) : {},
        manualAddress:
          addressProvider === 'manual'
            ? getManualAddressErrors(manualAddress)
            : {},
      };

      setFormErrors(errors);

      return !Object.values(errors).some(
        section => Object.keys(section).length > 0,
      );
    },
    [
      addressProvider,
      getGbgAddressErrors,
      getManualAddressErrors,
      getMovingDateErrors,
    ],
  );

  const valuesChangeHandler = useCallback(
    (section: keyof FormValues, shouldValidate?: boolean) => {
      return (values: FormValues[typeof section]) => {
        const newFormValues = { ...formValues, [section]: values };

        setFormValues(newFormValues);

        if (shouldValidate) {
          validateForm(newFormValues);
        }
      };
    },
    [formValues, validateForm],
  );

  const errorsChangeHandler = useCallback(
    (section: keyof FormErrors) => {
      return (errors: FormErrors[typeof section]) => {
        setFormErrors({ ...formErrors, [section]: errors });
      };
    },
    [formErrors],
  );

  const values: MovingHomeFormState = useMemo(
    () => ({
      formRef,
      formValues,
      formErrors,
      addressProvider,
      formSubmissionState,
      setFormSubmissionState,
      setAddressProvider,
      getGbgAddressErrors,
      getManualAddressErrors,
      getMovingDateErrors,
      validateForm,
      valuesChangeHandler,
      errorsChangeHandler,
    }),
    [
      addressProvider,
      errorsChangeHandler,
      formErrors,
      formSubmissionState,
      formValues,
      getGbgAddressErrors,
      getManualAddressErrors,
      getMovingDateErrors,
      validateForm,
      valuesChangeHandler,
    ],
  );

  return values;
}
